import { useState, useEffect, useCallback } from 'react';
import { getPersonasList } from '../endpoints/personas/list_GET.schema';
import type { PersonaWithSettings } from '../endpoints/personas/list_GET.schema';

export interface Participant {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  systemPrompt: string;
  isActive: boolean;
}

const STORAGE_KEY = 'taskingbot-active-participants';

export function useTaskingBotParticipants() {
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load real personas from DB on mount
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      try {
        const { personas } = await getPersonasList({}, { credentials: 'include' });

        if (cancelled) return;

        // Load active state from localStorage
        let activeIds: string[] = [];
        try {
          const stored = localStorage.getItem(STORAGE_KEY);
          if (stored) activeIds = JSON.parse(stored);
        } catch {
          /* ignore */
        }

        const mapped: Participant[] = personas
          .filter((p) => p.isEnabled)
          .map((p: PersonaWithSettings) => ({
            id: p.id,
            name: p.name,
            description: p.description || '',
            icon: p.icon,
            category: p.category,
            systemPrompt: p.systemPrompt,
            isActive: activeIds.includes(p.id),
          }));

        setParticipants(mapped);
      } catch (err) {
        console.error('[useTaskingBotParticipants] Failed to load personas:', err);
        if (!cancelled) setParticipants([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleParticipant = useCallback((participantId: string) => {
    setParticipants((prev) => {
      const updated = prev.map((p) =>
        p.id === participantId ? { ...p, isActive: !p.isActive } : p,
      );
      // Persist active IDs to localStorage
      try {
        const activeIds = updated.filter((p) => p.isActive).map((p) => p.id);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(activeIds));
      } catch (err) {
        console.error('Failed to save participant preferences:', err);
      }
      return updated;
    });
  }, []);

  const clearAllActive = useCallback(() => {
    setParticipants((prev) => prev.map((p) => ({ ...p, isActive: false })));
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([]));
    } catch (err) {
      console.error('Failed to clear participant preferences:', err);
    }
  }, []);

  const activeParticipants = participants.filter((p) => p.isActive);

  return {
    participants,
    activeParticipants,
    toggleParticipant,
    clearAllActive,
    isLoading,
  };
}
